// Industrial capacity-increase premium (产业用地提高容积) calculation.
//
// Per rules/RULES_INDUSTRIAL.md §4 and logic/CALCULATION_INDUSTRIAL.md
// §4, when an industrial parcel's volume is increased, the added GFA
// triggers a premium that depends on the parcel's main industrial
// land type (M1 / M0 / W1 / W0) and the item's supporting-facility
// classification. The scaling is applied per-item on top of the
// standard change-premium premium.
//
//   M1 / M0:
//     工业厂房 / 相应辅助设施          → 0
//     配套商业                        → 1.0   (base premium × 100%)
//     除配套商业外其他配套建筑         → 0.5   (base premium × 50%)
//
//   W1 / W0:
//     配套商业                        → 1.0
//     除配套商业外其他建筑面积         → 0.5
//
//   not flagged with isCapacityIncrease → no scaling, return basePremium.

import { MessageSink } from "./message-sink.js";

import type {
  CapacityIncreaseItemResult,
  IndustrialCapacityIncreaseInput,
  IndustrialCapacityIncreaseResult,
} from "../types/capacity-increase.js";
import type { CalculationResult } from "../types/calculation-result.js";
import type { BuildingItem } from "../types/building-item.js";
import type { MainIndustrialLandType } from "../types/enums.js";
import type { ValidationMessage } from "../types/validation.js";

import { calculateChangePremium } from "./change-premium.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Pure scaling helper: given a per-item base premium and the parcel's
 * main industrial land type, return the scaled premium according to
 * the M1/M0/W1/W0 table.
 *
 * - When the item is NOT flagged with `isCapacityIncrease`, the
 *   function is a no-op (returns `basePremium` unchanged, no message).
 * - When the item IS flagged but `mainIndustrialLandType` is missing,
 *   we emit an error and return `basePremium` (so the user still sees
 *   a number) — the error message flags the missing field.
 */
export function calculateIndustrialCapacityIncreasePremium(
  item: BuildingItem,
  basePremium: number,
  mainIndustrialLandType: MainIndustrialLandType | undefined,
  sink: MessageSink,
  field: string = "isCapacityIncrease",
): number {
  if (!item.isCapacityIncrease) return basePremium;

  if (mainIndustrialLandType === undefined) {
    sink.error(
      field,
      "产业用地提高容积计算需要 parcel.mainIndustrialLandType（M1 / M0 / W1 / W0）。",
      "请在宗地信息中填写 mainIndustrialLandType。",
    );
    return basePremium;
  }

  return scaleByMainType(item, basePremium, mainIndustrialLandType, sink, field);
}

/**
 * Compute the §2.1 change-premium for the input, then layer the
 * capacity-increase scaling on top. The wrapper:
 *   1. Runs the standard change-premium flow (so `base` carries the
 *      full per-item result for both before/after).
 *   2. For each item in `itemsAfter` flagged with `isCapacityIncrease`,
 *      computes the per-item premium (after − before, clamped to 0)
 *      and scales it via `calculateIndustrialCapacityIncreasePremium`.
 *   3. Sums the adjusted per-item premiums into `capacityIncreasePremium`.
 *
 * Items without `isCapacityIncrease` are returned in the per-item
 * breakdown with `scaled: false` and zero deltas.
 *
 * `mainIndustrialLandType` is read from `parcelAfter`.
 */
export function calculateChangePremiumWithCapacityIncrease(
  input: IndustrialCapacityIncreaseInput,
): IndustrialCapacityIncreaseResult {
  const base = calculateChangePremium(input);

  // Index beforeResult by itemId for the per-item diff.
  const beforeById = new Map<string, number>();
  for (const r of base.beforeResult.itemResults) {
    beforeById.set(r.itemId, r.itemLandPrice);
  }

  // Collect extra messages produced by the scaling layer so they
  // surface alongside the after snapshot.
  const afterWithExtras: ValidationMessage[] = [];
  const itemResults: CapacityIncreaseItemResult[] = input.itemsAfter.map(
    (item) =>
      scaleItem(
        item,
        base.afterResult,
        beforeById,
        input.parcelAfter.mainIndustrialLandType,
        afterWithExtras,
      ),
  );

  const capacityIncreasePremium = itemResults.reduce(
    (sum, r) => sum + r.adjustedPremium,
    0,
  );

  const note =
    base.note ??
    (base.difference < 0
      ? "差额为负，不再计收地价。"
      : undefined);

  const afterResultWithCapacityMessages: CalculationResult = {
    ...base.afterResult,
    validationMessages: [
      ...base.afterResult.validationMessages,
      ...afterWithExtras,
    ],
  };

  return {
    base: {
      ...base,
      afterResult: afterResultWithCapacityMessages,
    },
    capacityIncreasePremium,
    itemResults,
    note,
  };
}

// ---------------------------------------------------------------------------
// Internal scaling
// ---------------------------------------------------------------------------

function scaleByMainType(
  item: BuildingItem,
  basePremium: number,
  mainType: MainIndustrialLandType,
  sink: MessageSink,
  field: string,
): number {
  const facility = item.supportingFacilityType;

  if (mainType === "M1" || mainType === "M0") {
    if (facility === "工业厂房" || facility === "相应辅助设施") {
      sink.info(field, `M1/M0 工业厂房及辅助设施不计收地价（${item.id}）。`);
      return 0;
    }
    if (facility === "配套商业") {
      return basePremium;
    }
    // Any other supporting facility (or unspecified) on M1/M0 → 50%.
    return basePremium * 0.5;
  }

  if (mainType === "W1" || mainType === "W0") {
    if (facility === "配套商业") {
      return basePremium;
    }
    return basePremium * 0.5;
  }

  // Defensive: the union is exhausted above, so this is unreachable.
  return basePremium;
}

function scaleItem(
  item: BuildingItem,
  afterResult: CalculationResult,
  beforeById: Map<string, number>,
  mainType: MainIndustrialLandType | undefined,
  extraMessages: ValidationMessage[],
): CapacityIncreaseItemResult {
  if (!item.isCapacityIncrease) {
    return {
      itemId: item.id,
      basePremium: 0,
      adjustedPremium: 0,
      scaled: false,
      supportingFacilityType: item.supportingFacilityType,
      validationMessages: [],
    };
  }

  const beforeItemLandPrice = beforeById.get(item.id) ?? 0;
  const afterItem = afterResult.itemResults.find(
    (r) => r.itemId === item.id,
  );
  const afterItemLandPrice = afterItem?.itemLandPrice ?? 0;
  const basePremium = Math.max(afterItemLandPrice - beforeItemLandPrice, 0);

  // Per-item sink so the messages can be reported at item granularity
  // in the result and ALSO bubbled up to the after snapshot.
  const itemSink = new MessageSink();
  const adjustedPremium = calculateIndustrialCapacityIncreasePremium(
    item,
    basePremium,
    mainType,
    itemSink,
  );
  const itemMessages = itemSink.toArray();
  for (const m of itemMessages) {
    extraMessages.push(m);
  }

  return {
    itemId: item.id,
    basePremium,
    adjustedPremium,
    scaled: true,
    supportingFacilityType: item.supportingFacilityType,
    validationMessages: itemMessages,
  };
}